import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '@/lib/apiBase'
import { getAccessToken } from '@/lib/authToken'
import { createSocket, invalidationPredicate, socketOrigin, type TicketEventPayload } from '@/lib/socket'
import { TICKET_COUNTS_KEY } from '@/hooks/useTicketCounts'
import type { TicketCounts } from '@/api/ticketsApi'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/ToastProvider'

export type RealtimeStatus = 'connecting' | 'live' | 'offline'

function looksLikeCounts(payload: unknown): payload is TicketCounts {
  return typeof payload === 'object' && payload !== null && typeof (payload as TicketCounts).allOpen === 'number'
}

/**
 * One socket per signed-in session. Events invalidate TanStack Query keys;
 * after a reconnect everything live is refetched because events may have been
 * missed while offline.
 */
export function useRealtime(): RealtimeStatus {
  const { t } = useTranslation()
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [status, setStatus] = useState<RealtimeStatus>('connecting')
  const isAgent = user?.kind === 'AGENT'

  useEffect(() => {
    const token = getAccessToken()
    if (!isAuthenticated || !token) return
    const socket = createSocket(socketOrigin(API_BASE_URL, window.location.origin), token)
    let wasConnected = false

    socket.on('connect', () => {
      setStatus('live')
      if (wasConnected) {
        void queryClient.invalidateQueries({
          predicate: (q) => ['ticket-counts', 'tickets', 'ticket', 'ticket-messages'].includes(String(q.queryKey[0])),
        })
      }
      wasConnected = true
    })
    socket.on('disconnect', () => setStatus('offline'))
    socket.on('connect_error', () => setStatus('offline'))

    socket.on('queue.counts', (payload: unknown) => {
      // Agents receive the counts object; customers receive ids only and refetch.
      if (looksLikeCounts(payload)) queryClient.setQueryData(TICKET_COUNTS_KEY, payload)
      else void queryClient.invalidateQueries({ queryKey: TICKET_COUNTS_KEY })
    })
    for (const event of ['ticket.updated', 'ticket.message'] as const) {
      socket.on(event, (payload: TicketEventPayload) => {
        void queryClient.invalidateQueries({ predicate: invalidationPredicate(event, payload) })
      })
    }
    socket.on('sla.warning', (payload: TicketEventPayload) => {
      void queryClient.invalidateQueries({ predicate: invalidationPredicate('sla.warning', payload) })
      if (isAgent) {
        const key = payload?.ticketKey ?? payload?.key
        notify({
          title: t('realtime.slaWarning', { key: key ?? '' }),
          href: payload?.ticketId ? `/tickets/${key ?? payload.ticketId}` : undefined,
          tone: 'warning',
        })
      }
    })
    socket.on('mention', (payload: TicketEventPayload & { by?: string; byName?: string }) => {
      const key = payload?.ticketKey ?? payload?.key
      notify({
        title: t('realtime.mention', { key: key ?? t('realtime.aTicket') }),
        description: payload?.byName ?? payload?.by ?? undefined,
        href: payload?.ticketId ? `/tickets/${key ?? payload.ticketId}` : undefined,
      })
    })

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
    }
  }, [isAuthenticated, isAgent, queryClient, notify, t])

  return status
}
